import { Collapse, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type {
  OdFpfnReport as Report,
  PerceptionCaseDetailRow,
  PerceptionCategoryMetrics,
} from '@/apis/simulation';
import SideInfo from './SideInfo';
import OdFpfnCaseTable from './OdFpfnCaseTable';
import { fmtCount, fmtRatio } from '../format';

const categoryLabel = (category: string, cn: string | null): string =>
  cn && cn !== category ? `${cn}(${category})` : category;

export default function OdFpfnReport({
  report,
  byCategory,
  caseDetailRows,
  previewUrlMap,
  previewLoading,
  onViewLog,
}: {
  report: Report;
  byCategory: PerceptionCategoryMetrics[];
  caseDetailRows: PerceptionCaseDetailRow[];
  previewUrlMap: Record<number, string>;
  previewLoading: boolean;
  onViewLog: (taskId: number) => void;
}) {
  const isFp = report.cases.some((r) => r.mode === 'fp');
  const direction = isFp ? 'FP' : 'FN';
  // Checker verdict is per case, not per target type. Count the checker's
  // failed cases belonging to each category (a case belongs when that
  // category has at least one valid target in it; ALL covers every case).
  const failedCheckerCountByCategory = (category: string) => {
    if (category === 'ALL') return report.cases.filter((r) => !r.passed).length;
    const caseIds = new Set(
      caseDetailRows
        .filter((row) => row.category === category && (row.gt ?? 0) > 0)
        .map((row) => row.caseId || ''),
    );
    return report.cases.filter((r) => !r.passed && caseIds.has(r.caseId || '')).length;
  };

  const categoryColumns: ColumnsType<PerceptionCategoryMetrics> = [
    {
      title: '目标类型',
      dataIndex: 'category',
      key: 'category',
      width: 170,
      render: (_, r) => categoryLabel(r.category, r.categoryCn),
    },
    {
      title: isFp ? '标注误检目标' : '标注漏检目标',
      key: 'annotated',
      width: 110,
      align: 'right',
      render: (_, r) => fmtCount(r.extraCounts?.annotated_target_count),
    },
    { title: isFp ? '有效误检目标' : '有效漏检目标', dataIndex: 'gt', width: 110, align: 'right', render: fmtCount },
    { title: isFp ? '复现误检目标' : '检出目标', dataIndex: 'det', width: 110, align: 'right', render: fmtCount },
    {
      title: isFp ? '未复现误检目标' : '未检出目标',
      key: 'unmatched',
      width: 130,
      align: 'right',
      render: (_, r) => fmtCount(r.extraCounts?.unmatched_target_count),
    },
    {
      title: isFp ? '误检复现率' : '漏检率',
      key: 'issue_rate',
      width: 100,
      align: 'right',
      // Runtime maps problem targets to fp (FP mode) / fn (FN mode). The
      // generic unmatched count is the opposite direction and would always be
      // 100% for FP scenes with no reproduction.
      render: (_, r) => {
        const numerator = isFp ? r.fp : r.fn;
        const denominator = r.gt;
        return numerator !== null && numerator !== undefined && denominator
          ? fmtRatio(numerator / denominator) : fmtRatio(null);
      },
    },
    { title: '覆盖 case 数', dataIndex: 'caseCount', width: 100, align: 'right', render: fmtCount },
    {
      title: isFp ? '存在误检目标 case 数' : '存在漏检目标 case 数',
      dataIndex: 'failedCaseCount',
      width: 150,
      align: 'right',
      render: fmtCount,
    },
    {
      title: '不通过 case 数',
      key: 'failedCheckerCases',
      width: 120,
      align: 'right',
      // Runtime failed_case_count describes target-level problem cases only
      // (e.g. FP cases failing by alignment are absent). Use checker verdicts
      // instead so the column always matches the checker's pass/fail result.
      render: (_, r) => fmtCount(failedCheckerCountByCategory(r.category)),
    },
  ];

  return (
    <>
      <div className="pe-report-card">
        <div className="pe-report-card-title">OD {direction} issue_related 评测</div>
        <div className="pe-sides">
          <SideInfo header={report.header} side="current" />
        </div>
      </div>
      <div className="pe-report-card">
        <div className="pe-report-card-title">分类别指标</div>
        <Table<PerceptionCategoryMetrics>
          rowKey="category"
          size="small"
          pagination={false}
          tableLayout="fixed"
          dataSource={byCategory}
          columns={categoryColumns}
          scroll={{ x: 900 }}
          rowClassName={(r) => (r.category === 'ALL' ? 'pe-row-all' : '')}
          expandable={{
            expandedRowRender: (r) => (
              <OdFpfnCaseTable
                cases={report.cases}
                isFp={isFp}
                category={r.category}
                categoryRows={caseDetailRows.filter((row) => row.category === r.category)}
                previewUrlMap={previewUrlMap}
                previewLoading={previewLoading}
                onViewLog={onViewLog}
              />
            ),
            rowExpandable: (r) => {
              if (r.category === 'ALL') return report.cases.length > 0;
              return caseDetailRows.some(
                (row) => row.category === r.category && (row.gt ?? 0) > 0,
              );
            },
          }}
        />
      </div>
      <Collapse
        ghost
        items={[{
          key: 'od-fpfn-metrics',
          label: <span style={{ fontSize: 12, color: '#6b6f76' }}>指标说明</span>,
          children: (
            <div className="pe-metric-explain">
              {isFp ? (
                <>
                  <div>· 标注误检目标：annotation 中标注的误检目标出现次数；同一目标跨帧出现会按帧累计，不是去重后的目标数。</div>
                  <div>· 有效误检目标：标注误检目标中，通过类型、ROI 与框有效性过滤后参与评测的次数。</div>
                  <div>· 复现误检目标：有效误检目标中，感知在标注框/区域内检出障碍物的次数；越少越好。</div>
                  <div>· 未复现误检目标：有效误检目标中，感知未检出障碍物的次数。</div>
                  <div>· 误检复现率：复现误检目标 / 有效误检目标；越低越好。</div>
                  <div>· 逐 case 表「问题目标数」：包含缺感知帧、无效标注和复现误检等所有 checker 问题目标，不等于复现误检目标；具体构成见「不通过原因」。</div>
                  <div>· 逐 case 表「复现帧率」：复现误检帧数 / 有效且对齐帧数；越低越好。</div>
                  <div>· 逐 case 表「帧对齐率」：对齐帧数 / 标注帧数；过低会导致 checker 不通过。</div>
                  <div>· 不通过 case 数：该目标类型下 checker 判定不通过的 case 数；ALL 为整个 Job 的不通过 case 数。</div>
                </>
              ) : (
                <>
                  <div>· 标注漏检目标：annotation 中标注的漏检目标出现次数；同一目标跨帧出现会按帧累计，不是去重后的目标数。</div>
                  <div>· 有效漏检目标：标注漏检目标中，通过类型映射、ROI 与框有效性过滤后参与评测的次数。</div>
                  <div>· 检出目标：有效漏检目标中，感知已稳定检出的次数；越多越好。</div>
                  <div>· 未检出漏检目标：有效漏检目标中，感知仍未稳定检出的次数；越少越好。</div>
                  <div>· 漏检率：未检出漏检目标 / 有效漏检目标；越低越好。</div>
                  <div>· 逐 case 表「问题目标数」：包含缺感知帧、无效标注和未检出漏检等所有 checker 问题目标，不等于未检出漏检目标；具体构成见「不通过原因」。</div>
                  <div>· 逐 case 表「匹配帧率」：全部目标均检出的帧数 / 有效且对齐帧数；越高越好。</div>
                  <div>· 逐 case 表「帧对齐率」：对齐帧数 / 标注帧数；过低会导致 checker 不通过。</div>
                  <div>· 不通过 case 数：该目标类型下 checker 判定不通过的 case 数；ALL 为整个 Job 的不通过 case 数。</div>
                </>
              )}
              <div>· 展开行内为该目标类型的逐 case 明细；ALL 行展示全部 case。</div>
            </div>
          ),
        }]}
      />
    </>
  );
}
